import { shallowRef } from 'vue'
import type { ElementPick, PlacementPick, PlacementPosition } from '@dashboard/services/elementPickerProtocol'

export type { ElementPick, PlacementPick } from '@dashboard/services/elementPickerProtocol'
export type PickElementsOptions = {
  url: string
  restrictTo?: string
  multiple?: boolean
  initial?: string
  placement?: { label: string; position?: PlacementPosition; single?: boolean }
}
export type PlacementElementsOptions = PickElementsOptions & {
  placement: NonNullable<PickElementsOptions['placement']>
}
export type PickElementsResult = { selector: string; picks: ElementPick[] }
export type PlacementElementsResult = { picks: PlacementPick[] }
export type ElementPickerResult = PickElementsResult | PlacementElementsResult

type PickerSession = { id: number; options: PickElementsOptions; resolve: (result: ElementPickerResult | null) => void }
export const elementPickerSession = shallowRef<PickerSession | null>(null)
let nextSessionId = 0

/**
 * A surface that already shows the page being picked from and answers the pick itself, such as the page editor's
 * canvas for the hosted page it edits. A claimed pick opens no picker dialog.
 */
export type ElementPickerBridge = {
  claims: (options: PickElementsOptions) => boolean
  pick: (options: PickElementsOptions) => Promise<PickElementsResult | null>
}
const bridges = new Set<ElementPickerBridge>()

export function registerElementPickerBridge(bridge: ElementPickerBridge): () => void {
  bridges.add(bridge)
  return () => {
    bridges.delete(bridge)
  }
}

export function finishElementPicker(result: ElementPickerResult | null): void {
  const session = elementPickerSession.value
  elementPickerSession.value = null
  session?.resolve(result)
}

function pickElements(options: PlacementElementsOptions): Promise<PlacementElementsResult | null>
function pickElements(options: PickElementsOptions & { placement?: undefined }): Promise<PickElementsResult | null>
function pickElements(options: PickElementsOptions): Promise<ElementPickerResult | null>
function pickElements(options: PickElementsOptions): Promise<ElementPickerResult | null> {
  finishElementPicker(null)
  const bridge = options.placement ? undefined : [...bridges].find((candidate) => candidate.claims(options))
  if (bridge) {
    return bridge.pick({ ...options })
  }
  return new Promise<ElementPickerResult | null>((resolve) => {
    elementPickerSession.value = { id: ++nextSessionId, options: { ...options }, resolve }
  })
}

export function useElementPicker(): { pickElements: typeof pickElements } {
  return { pickElements }
}
